import React, { useEffect, useState } from 'react';
import { inputCls } from '../shared/FormKit';
import { fetchJSON } from '../../utils/api';
import { QM_TEAM, QM_TEAM_PICK, QM_TEAM_NAME } from '../../utils/staffGrades';

// The shared reviewer picker of the chosen-reviewer flows: the submitter
// names who approves (GET /api/workflow/reviewer-options — fetched here once
// per roster and cached, so every form gets the list without wiring its own
// options endpoint, the way QcForm's EQR select works). Two rosters:
//   'firm'   (default) Partners and Directors — QC6/QC7, QC Amendment, QC
//            Addendum, Signed Financial Statements, QC35 and their amendments
//   'master' Partners, plus one QM Team option — New Client, Client
//            Amendment, Engagement Registration, Engagement Amendment. The
//            QM Team option posts QM_TEAM_PICK: anyone serving in the QM
//            department at review time may approve. It is disabled while the
//            department has nobody serving.
//
// Props:
//   value / onChange(staffIdString)  controlled staff id ('' = none)
//   roster                          'firm' | 'master' (see above)
//   grade                           'partner' | 'director' — list only that
//                                   grade (the QC reviewer plan names the
//                                   grade each stage calls for); omitted =
//                                   the whole roster
//   excludeIds                      staff ids hidden from the list (the other
//                                   picks of a multi-reviewer flow — the
//                                   submitter themselves is a valid pick)
//   disabled / placeholder          plain <select> passthroughs
// The list is grouped Partners first, then Directors (firm) or the QM Team
// (master), each person carrying the designation so two same-named people
// stay tellable apart.

const cache = {};
const inflight = {};
const loadReviewers = (roster) => {
  if (cache[roster]) return Promise.resolve(cache[roster]);
  if (!inflight[roster]) {
    inflight[roster] = fetchJSON(`/api/workflow/reviewer-options${roster === 'master' ? '?roster=master' : ''}`)
      .then((data) => { cache[roster] = data.reviewers || []; return cache[roster]; })
      .catch(() => { inflight[roster] = null; return []; });
  }
  return inflight[roster];
};

export const useReviewerOptions = (roster = 'firm') => {
  const [reviewers, setReviewers] = useState(cache[roster] || []);
  useEffect(() => {
    let alive = true;
    loadReviewers(roster).then((list) => { if (alive) setReviewers(list); });
    return () => { alive = false; };
  }, [roster]);
  return reviewers;
};

// The reviewer select's value for a request already on file: the QM Team
// pick when the team reviews it, else the named person's id ('' = none).
export const reviewerPickOf = (request) => {
  if (!request) return '';
  if (request.reviewerTeam === QM_TEAM) return QM_TEAM_PICK;
  return request.reviewerStaffId ? String(request.reviewerStaffId) : '';
};

// Does the master-roster pick put the QM department on the request — the QM
// Team, or a Partner who serves in QM? Engagement Registration and Amendment
// ask the screening question only then; with any other reviewer only the
// screening document upload shows. false until the roster has loaded or while
// nobody is picked.
export const useIsQmReviewer = (value) => {
  const reviewers = useReviewerOptions('master');
  if (!value) return false;
  if (value === QM_TEAM_PICK) return true;
  const row = reviewers.find((r) => String(r.staffId) === String(value));
  return !!(row && row.qmDepartment);
};

// A Modify modal pre-fills the reviewer already named on the request. When
// that pick is not one the dropdown offers — a person not of the grade the
// picker lists, someone who left the roster, a QM member named one by one
// before the QM Team option existed, or the QM Team while nobody serves in
// QM — it is cleared once the roster has loaded, so the form asks for a new
// one instead of holding a value the dropdown cannot show.
export const useClearOffRosterPick = (value, onChange, grade = null, roster = 'firm') => {
  const reviewers = useReviewerOptions(roster);
  useEffect(() => {
    if (!value || reviewers.length === 0) return;
    const ok = value === QM_TEAM_PICK
      ? roster === 'master' && reviewers.some((r) => r.qmDepartment)
      : reviewers.some((r) => String(r.staffId) === String(value)
        && (roster === 'master' ? r.role === 'partner' : (!grade || r.role === grade)));
    if (!ok) onChange('');
  }, [reviewers, value, grade, roster, onChange]);
};

// The hint under the Reviewer field of the master-data flows (New, Modify).
export const MASTER_REVIEWER_HINT = "You pick who reviews this request: one of the firm's Partners, or the QM Team (anyone in the QM department may review it).";

const GRADE_PLACEHOLDER = { partner: 'Select a Partner…', director: 'Select a Director…' };
// The second group of each roster (Partners always come first).
const SECOND_GROUP = {
  firm: { role: 'director', label: 'Directors', placeholder: 'Select a Partner or Director…' },
  master: { role: 'qm', label: 'QM', placeholder: 'Select a Partner or the QM Team…' },
};

const ReviewerSelect = ({
  value,
  onChange,
  roster = 'firm',
  grade = null,
  excludeIds = [],
  disabled = false,
  placeholder = null,
}) => {
  const reviewers = useReviewerOptions(roster);
  const second = SECOND_GROUP[roster] || SECOND_GROUP.firm;
  const excluded = new Set((excludeIds || []).map((n) => Number(n)).filter(Boolean));
  const visible = reviewers.filter((r) => !excluded.has(Number(r.staffId)) && (!grade || r.role === grade));
  const partners = visible.filter((r) => r.role === 'partner');
  const others = visible.filter((r) => r.role === second.role);
  // Master roster: the QM department is one option, not a list of people.
  const qmTeamSize = reviewers.filter((r) => r.qmDepartment).length;
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={inputCls}
    >
      <option value="">{placeholder || GRADE_PLACEHOLDER[grade] || second.placeholder}</option>
      {partners.length > 0 && (
        <optgroup label="Partners">
          {partners.map((r) => (
            <option key={r.staffId} value={r.staffId}>{r.name} ({r.position})</option>
          ))}
        </optgroup>
      )}
      {roster === 'master' && reviewers.length > 0 && (
        <optgroup label={second.label}>
          <option value={QM_TEAM_PICK} disabled={qmTeamSize === 0}>
            {qmTeamSize === 0 ? `${QM_TEAM_NAME} (no serving members)` : QM_TEAM_NAME}
          </option>
        </optgroup>
      )}
      {roster !== 'master' && others.length > 0 && (
        <optgroup label={second.label}>
          {others.map((r) => (
            <option key={r.staffId} value={r.staffId}>{r.name} ({r.position})</option>
          ))}
        </optgroup>
      )}
    </select>
  );
};

export default ReviewerSelect;
